import type { ComponentProps, ComponentType, HTMLAttributes, ReactNode } from 'react';
import { kebabCase, map } from 'lodash/fp';

import { LocalizedCurrencyValue, LocalizedMarkdown, MultiCurrencyAmount, TextKey } from '@mews-distributor/common';
import { LocalizedText } from '@mews/framework';
import type { LocalizedPropertyObject } from '@mews/framework';
import { Body, Highlight, NestedCard, Stack, Title, Typography } from '@optimus-web/core';
import type { IconButtonProps } from '@optimus-web/core';
import { InfoBox } from '../InfoBox';
import type { InfoBoxProps } from '../InfoBox';

type ExtendedInfoBoxProps = InfoBoxProps & HTMLAttributes<HTMLDivElement> & Pick<IconButtonProps, 'accessibleText'>;
type LocalizedMarkdownPropsFixed = Omit<ComponentProps<typeof LocalizedMarkdown>, 'property'> & {
    property: LocalizedPropertyObject | null;
};

const ExtendedInfoBox = InfoBox as unknown as ComponentType<ExtendedInfoBoxProps>;
const FixedLocalizedMarkdown = LocalizedMarkdown as unknown as ComponentType<LocalizedMarkdownPropsFixed>;

interface RateComparePriceProps {
    value: MultiCurrencyAmount | null;
    [key: string]: unknown;
}

interface RateCompareFactoryComponents {
    Price?: ComponentType<RateComparePriceProps>;
}

export interface RateCompareProps extends Omit<ExtendedInfoBoxProps, 'children'> {
    competitors: string[];
    competitorsPrice: MultiCurrencyAmount | null;
    description?: LocalizedPropertyObject | null;
    lowestRoomPrice: MultiCurrencyAmount | null;
    priceAdjustment?: number;
}

const mapCompetitors = (price: ReactNode) =>
    map((name: string) => (
        <Stack spacing="100" distribution="space-between" key={name}>
            <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                {name}
            </Typography>
            <Typography
                textStyle="bodyMediumStrong"
                variant="secondary"
                data-test-id={`rate-compare-prices-values-${kebabCase(name)}`}
                as="div"
            >
                {price}
            </Typography>
        </Stack>
    ));

export const rateCompareFactory =
    ({ Price = LocalizedCurrencyValue }: RateCompareFactoryComponents = {}) =>
    ({
        competitors,
        competitorsPrice,
        description,
        opened,
        lowestRoomPrice,
        toggleOpened,
        ...other
    }: RateCompareProps) => {
        const descriptionContent =
            description != null && Object.values(description).some((value) => value.trim() !== '') ? description : null;

        let descriptionNode: ReactNode = null;

        if (descriptionContent != null) {
            descriptionNode = (
                <NestedCard variant="highlighted">
                    <Highlight size="small">
                        <LocalizedText textKey={TextKey.BookOnlineBonuses} />
                    </Highlight>
                    <Body size="medium" overflowWrap="break-word">
                        <FixedLocalizedMarkdown property={descriptionContent} />
                    </Body>
                </NestedCard>
            );
        }

        return (
            <ExtendedInfoBox
                opened={opened}
                toggleOpened={toggleOpened}
                data-test-id="rate-compare"
                accessibleText={
                    opened === true ? (
                        <LocalizedText textKey={TextKey.HideRateComparison} />
                    ) : (
                        <LocalizedText textKey={TextKey.SeeRateComparison} />
                    )
                }
                {...other}
                role="complementary"
            >
                <Stack spacing="200" vertical>
                    <Title level={3}>
                        <LocalizedText textKey={TextKey.BookOnlineGetMore} />
                    </Title>
                    {descriptionNode}
                    <div>
                        <Typography textStyle="bodySmall" variant="secondary" as="div">
                            <LocalizedText textKey={TextKey.OurPrice} />
                        </Typography>
                        <Highlight size="medium">
                            <Price value={lowestRoomPrice} data-test-id="rate-compare-our-price-value" />
                        </Highlight>
                    </div>
                    <div>
                        <Highlight size="small">
                            <LocalizedText textKey={TextKey.ComparePrices} />
                        </Highlight>
                        {mapCompetitors(<Price value={competitorsPrice} />)(competitors)}
                    </div>
                </Stack>
            </ExtendedInfoBox>
        );
    };

export const RateCompare = rateCompareFactory();
